import { Injectable } from '@angular/core';
import { PaymentDocumentHelperService } from './payment-document-helper.service';
import {
  PaymentPreviewV1Response,
  SessionDocumentInfo,
  PaymentRulesResponseV1,
  ValidateInvoicePayableAmountErrors,
  AppliedDiscountMeta,
} from '../models';
import { getEditableAmountErrorMessage, roundTo2Decimals } from '../helpers';
import { MessageService } from '@arc/aureus';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class InvoiceHelperService {
  constructor(
    private paymentDocumentService: PaymentDocumentHelperService,
    private messageService: MessageService,
    private translationService: TranslateService,
  ) {}

  validateInvoiceEditedPayableAmount(
    editedDocument: SessionDocumentInfo,
    paymentPreview: PaymentPreviewV1Response,
    selectedDocuments: SessionDocumentInfo[],
    discountMeta: AppliedDiscountMeta[],
    subTotal: number,
    paymentRules: PaymentRulesResponseV1,
  ) {
    let splitDocuments: SessionDocumentInfo[];
    let splitSubTotal: number;

    if (paymentPreview?.payments.length > 1) {
      splitDocuments = selectedDocuments.filter((document) =>
        paymentPreview.payments
          .find((p) =>
            p.payment.payables.some((d) => d.burn === editedDocument.urn),
          )
          .payment.payables.map((p) => p.burn)
          .includes(document.urn),
      );
      splitSubTotal = this.paymentDocumentService.getSubTotal(splitDocuments);
    } else {
      splitDocuments = selectedDocuments;
      splitSubTotal = subTotal;
    }

    const errors = this.validateInvoicePayableAmount(
      editedDocument,
      splitDocuments,
      discountMeta,
      splitSubTotal,
      paymentRules,
    );

    if (
      errors.isNegativePayment ||
      errors.isZeroDollarPayment ||
      errors.isOverPayment ||
      errors.isShortPayment
    ) {
      return errors;
    }

    return null;
  }

  public displayInvoicePayableAmountError(
    errors: ValidateInvoicePayableAmountErrors,
    hasDocumentsFromMultipleAccounts: boolean,
    isZeroDollarPaymentEnabled: boolean,
  ) {
    const errorKey = this.getInvoicePayableAmountErrorMessage(
      errors,
      hasDocumentsFromMultipleAccounts,
      isZeroDollarPaymentEnabled,
    );

    // this is a TEMPORARY fix to avoid show multiple messages because we are calling multiple times the preview endpoint.
    // We are using this to show the message and dismiss others with the same text
    // see: https://aureus.aws-dev.billtrust.com/components/message
    this.messageService.open(
      this.translationService.instant(errorKey) as string,
      null,
      {
        severity: 'danger',
        channel: 'invoiceError',
        duration: 0,
        dismissOnNavigate: true,
        dismissOthers: true,
      },
    );
  }

  private getInvoicePayableAmountErrorMessage(
    errors: ValidateInvoicePayableAmountErrors,
    hasDocumentsFromMultipleAccounts: boolean,
    isZeroDollarPaymentEnabled: boolean,
  ) {
    if (errors.isNegativePayment || errors.isZeroDollarPayment) {
      const errorKey = getEditableAmountErrorMessage(
        hasDocumentsFromMultipleAccounts,
        isZeroDollarPaymentEnabled,
      );
      return errorKey;
    }
    if (errors.isShortPayment) {
      return 'ARC_PAYMENT_EDIT_PAYABLE_SHORT_PAYMENT_ERROR';
    }
    if (errors.isOverPayment) {
      return 'ARC_PAYMENT_EDIT_PAYABLE_OVER_PAYMENT_ERROR';
    }

    return null;
  }

  private validateInvoicePayableAmount(
    editedDocument: SessionDocumentInfo,
    selectedDocuments: SessionDocumentInfo[],
    discountMeta: AppliedDiscountMeta[],
    subTotal: number,
    paymentRules: PaymentRulesResponseV1,
  ) {
    const allowZeroDollarPayments =
      paymentRules?.settings.generalPaymentSettings.zeroDollarPaymentsEnabled;
    const allowShortPay =
      paymentRules?.settings.shortAndOverPaySettings?.allowShortPay;
    const allowOverPay =
      paymentRules?.settings.shortAndOverPaySettings?.allowOverPay;

    const currentPayableAmount = selectedDocuments.find(
      (x) => x.invoiceNumber === editedDocument.invoiceNumber,
    ).payableAmount;

    const editedDifference = roundTo2Decimals(
      currentPayableAmount - editedDocument.payableAmount,
    );
    const editedSubTotal = roundTo2Decimals(subTotal - editedDifference);
    const discountedAmount = discountMeta?.find(
      (d) => d.urn === editedDocument.urn,
    )?.discountedPayableAmount;

    const errors = {
      isNegativePayment: editedSubTotal < 0,
      isZeroDollarPayment: !allowZeroDollarPayments && editedSubTotal === 0,
      isOverPayment:
        !allowOverPay &&
        !editedDocument.isFullInvoiceDispute &&
        editedDocument.payableAmount >
          editedDocument.originalSessionPayableAmount,
      isShortPayment:
        !allowShortPay &&
        editedDocument.payableAmount <
          (discountedAmount ?? editedDocument.originalPayableAmount),
    };

    return errors;
  }
}
